import { AnalyticIdentifier } from '@common/neo.tax';
import {
  Button,
  colors,
  ControlledInput,
  ControlledTextArea,
  InputFormContainer,
  ModalActions,
  ModalHeader,
  Stack,
  Typography,
} from '@ui-components/neo.tax';
import { ReactComponent as Presentation } from '@ui-components/neo.tax/dist/icons/presentation-projector-screen-hierarchy.svg';
import { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { useAnalyticId } from '@neo.tax/shared/state';

import { Modal } from './project-creation-modal.styled';

type ProjectCreationForm = {
  name: string;
  description: string;
};

type ProjectCreationModalProps = {
  isOpen: boolean;
  onClose: () => void;
  submit: ({ description, name }: ProjectCreationForm) => Promise<void>;
  loading?: boolean;
};

export const ProjectCreationModal = ({
  isOpen,
  loading,
  onClose,
  submit,
}: ProjectCreationModalProps): React.ReactElement => {
  const { control, handleSubmit, reset } = useForm<ProjectCreationForm>({
    mode: 'onSubmit',
    defaultValues: {
      name: '',
      description: '',
    },
  });

  const nameId = useAnalyticId(AnalyticIdentifier.ENTERPRISE_CREATE_PROJECT_NAME);
  const descriptionId = useAnalyticId(AnalyticIdentifier.ENTERPRISE_CREATE_PROJECT_DESCRIPTION);
  const createAscProjectAnalyticId = useAnalyticId(AnalyticIdentifier.CREATE_PROJECT_BUTTON);

  const handleCancel = (): void => {
    reset();
    onClose();
  };

  const onSubmit: SubmitHandler<ProjectCreationForm> = async (data) => {
    await submit(data);
    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  return (
    <Modal isOpen={isOpen} setIsOpen={onClose}>
      <Stack as="form" direction="column" gap={2} onSubmit={handleSubmit(onSubmit)}>
        <ModalHeader
          icon={<Presentation width={24} color={colors.blue[600]} />}
          iconBgColor={colors.blue[50]}
          title={
            <Typography fontSize="xl" fontWeight="medium">
              Create New Project
            </Typography>
          }
        />

        <Stack direction="column">
          <ControlledInput
            control={control}
            id={nameId}
            name="name"
            label="Project Name"
            rules={{ required: 'Please provide a name for this project' }}
          />
          <InputFormContainer label="Project Description" inputId={descriptionId}>
            <ControlledTextArea
              control={control}
              name="description"
              id={descriptionId}
              rows={4}
              placeholder=""
              rules={{ required: 'Please describe the project' }}
            />
          </InputFormContainer>
        </Stack>

        <ModalActions hasTopBorder={false}>
          <Button variant="white" rounded="regular" size="base" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            rounded="regular"
            size="base"
            type="submit"
            disabled={loading}
            id={createAscProjectAnalyticId}
          >
            {loading ? 'Creating...' : 'Create Project'}
          </Button>
        </ModalActions>
      </Stack>
    </Modal>
  );
};
